import { Injectable } from '@angular/core';
import { FilterConfigModel, FilterModel } from './Inbuilt-report-model';
import { Observable, Subject } from 'rxjs';

interface TreeNode {
    id: string;
    name: string;
    children?: TreeNode[];
  }

@Injectable({
  providedIn: 'root'
})
export class InbuiltUtilityService {

  private norm = (v: string) => (v ?? '').toLowerCase().replace(/[\s_\-]+/g, '');
  private isHierarchalType = (type: any) => {
    const value = String(type ?? '').toLowerCase();
    return value === 'hierarchal' || value === 'hierarchal-delimiter';
  };
  public cachedData = new Map<string, any>();
  private selectedFilterEventMap = new Map<string, any>();
  private selectedFilterEventSubject = new Subject<any>();
  constructor() {
    
   }

  private normalizeFilterMetadata(filters: FilterModel[]): FilterModel[] {
    return (filters ?? []).map((filter: FilterModel) => {
      const nameMeta = this.resolveObjectMapping(filter?.name);
      const queryMeta = this.resolveObjectMapping(filter?.queryKey);
      filter.name = nameMeta.displayKey;
      filter.queryKey = queryMeta.queryKey || nameMeta.queryKey;

      return filter;
    });
  }

  private getHierarchyColumnsFromFilter(filter: any): { displayKeys: string[]; queryKeys: string[] } {
    const displayKeys: string[] = [];
    const queryKeys: string[] = [];

    if (filter?.column && typeof filter.column === 'object' && !Array.isArray(filter.column)) {
      Object.entries(filter.column).forEach(([display, query]) => {
        const displayKey = String(display ?? '').trim();
        if (!displayKey) return;
        const queryKey = String(query ?? displayKey).trim() || displayKey;
        displayKeys.push(displayKey);
        queryKeys.push(queryKey);
      });

      if (displayKeys.length > 0) {
        return { displayKeys, queryKeys };
      }
    }

    const fallback = this.resolveHierarchyPathMapping(filter?.column);
    fallback.displayPath
      .split(':')
      .map((part: string) => part.trim())
      .filter(Boolean)
      .forEach((part: string) => {
        displayKeys.push(part);
        queryKeys.push(part);
      });

    return { displayKeys, queryKeys };
  }

  private resolveObjectMapping(raw: any): { displayKey: string; queryKey: string } {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const entries = Object.entries(raw);
      if (entries.length > 0) {
        const [displayKey, queryKey] = entries[0];
        return {
          displayKey: String(displayKey ?? ''),
          queryKey: String(queryKey ?? displayKey ?? '')
        };
      }
    }

    const key = String(raw ?? '');
    return { displayKey: key, queryKey: key };
  }

  private resolveHierarchyPathMapping(raw: any): { displayPath: string; queryPath: string } {
    const pairs: Array<[string, string]> = [];

    const addPair = (display: any, query: any) => {
      const displayKey = String(display ?? '').trim();
      if (!displayKey) return;
      const queryKey = String(query ?? displayKey).trim() || displayKey;
      pairs.push([displayKey, queryKey]);
    };

    const addFromAny = (value: any) => {
      if (value == null) return;

      if (Array.isArray(value)) {
        value.forEach((item: any) => addFromAny(item));
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return;
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try {
            addFromAny(JSON.parse(trimmed));
            return;
          } catch {
            // fall through to legacy colon handling
          }
        }
        trimmed.split(':').map((part: string) => part.trim()).filter(Boolean).forEach((part: string) => addPair(part, part));
        return;
      }

      if (typeof value === 'object') {
        Object.entries(value).forEach(([display, query]) => addPair(display, query));
      }
    };

    addFromAny(raw);

    const displayPath = pairs.map(([display]) => display).join(':');
    const queryPath = pairs.map(([, query]) => query).join(':');

    return {
      displayPath,
      queryPath: queryPath || displayPath
    };
  }

  setFilteredDropdownData(initialAttributeData: any, cascadeData: any, allFilters: any) {
    const normalizedFilters = this.normalizeFilterMetadata(allFilters || []);
    normalizedFilters.forEach((filter :FilterModel)=> {
          if(filter.name.toLowerCase() == 'attributes'){
              filter.dropdownData = initialAttributeData.column ? initialAttributeData.column.map((item: any) => ({ 'name': item })) : [];
              filter.default_selection = initialAttributeData.default_selection ? initialAttributeData.default_selection : [];
              filter.initialData = [...filter.dropdownData];
              filter.sorted = false;
              filter.initial_default_selection = initialAttributeData.default_selection ? initialAttributeData.default_selection : [];
              filter.isLoading = false;
              filter.resetCounter = (filter.resetCounter || 0) + 1;
            }else{
            filter.dropdownData = this.getFilterFieldsFromCascadeData(filter, filter.type, cascadeData);
            filter.initialData = [...filter.dropdownData];
            filter.sorted = true;
            filter.initial_default_selection = filter.default_selection; // preserve each filter's own original default for reset
            filter.isLoading = false;
            filter.resetCounter = (filter.resetCounter || 0) + 1;
          }
        });
        return normalizedFilters;
  }

  getFilterFieldsFromCascadeData(filter: FilterModel, filterType: string, cascadeData: any): Array<any> {
    if (!cascadeData || cascadeData.length === 0) return [];
    const filterName = filter.name
    if (filterType === 'hierarchal-delimiter') {
      return this.buildTreeForHierarchyDelimiter(filter, cascadeData, String(filter?.splitClause ?? ''));
    }

    if (filterType === 'hierarchal') {
      // Build a deduplicated tree in a single pass — no per-item recursion
      const hierarchyColumns = this.getHierarchyColumnsFromFilter(filter);
      const res = this.buildHierarchyTree(cascadeData, hierarchyColumns.displayKeys, hierarchyColumns.queryKeys);
      return res;
    }

    // Resolve column keys from first matching item: display key (nameKey) and value key (valueKey)
    const normName = this.norm(filterName);
    let nameKey: string | undefined;
    let valueKey: string | undefined;
    
    // If filter.queryKey is provided and differs from filter.name, use it as the value field lookup key
    const hasExplicitValueKey = !!(filter.queryKey && filter.queryKey !== filter.name);
    const normValueKey = hasExplicitValueKey && filter.queryKey ? this.norm(filter.queryKey) : undefined;
    
    for (const item of cascadeData) {
      if (!nameKey) {
        nameKey = Object.keys(item).find(key => this.norm(key) === normName);
        if (nameKey) {
          if (hasExplicitValueKey) {
            // Use queryKey as the value field name: look for exact cascade field matching queryKey
            valueKey = Object.keys(item).find(key => this.norm(key) === normValueKey) || nameKey;
          } else {
            // Fall back to existing logic: look for field ending in id or use nameKey
            valueKey = Object.keys(item).find(key => this.norm(key) === `${normName}id`) || nameKey;
          }
          break;
        }
      }
    }
    if (!nameKey) return [];

    // Use a Set for O(1) dedup instead of filter+findIndex O(n²)
    const seen = new Set<string>();
    const result: Array<any> = [];
    for (const item of cascadeData) {
      const val = item[nameKey];
      if (val !== undefined && val !== null && !seen.has(String(val))) {
        seen.add(String(val));
        result.push({ name: val, value: item[valueKey!] });
      }
    }
    return result;
  }

  private buildHierarchyTree(cascadeData: any[], keyParts: string[], queryKeyParts: string[]): any[] {
    if (!keyParts || keyParts.length === 0) return [];

    // Pre-resolve actual column names once from the first item that has them,
    // instead of calling Object.keys().find() for every row at every level.
    const resolvedCols: (string | undefined)[] = new Array(keyParts.length).fill(undefined);
    const resolvedValueCols: (string | undefined)[] = new Array(keyParts.length).fill(undefined);
    for (const item of cascadeData) {
      for (let i = 0; i < keyParts.length; i++) {
        if (resolvedCols[i] === undefined) {
          resolvedCols[i] = Object.keys(item).find(k => this.norm(k) === this.norm(keyParts[i]));
        }
        if (resolvedValueCols[i] === undefined) {
          const queryKey = queryKeyParts[i] || keyParts[i];
          resolvedValueCols[i] = Object.keys(item).find(k => this.norm(k) === this.norm(queryKey));
        }
      }
      if (resolvedCols.every(c => c !== undefined)) break;
    }

    const roots: any[] = [];
    // nodeMap: nodeId → node — O(1) lookup replaces O(n) Array.find() at every level
    const nodeMap = new Map<string, any>();

    for (const item of cascadeData) {
      const rootCol = resolvedCols[0];
      if (!rootCol || !item[rootCol]) continue;

      const rootName: string = item[rootCol];
      const rootValueCol = resolvedValueCols[0] || rootCol;
      const rootId = String(item[rootValueCol] ?? rootName);
      const rootMapKey = rootId;

      if (!nodeMap.has(rootMapKey)) {
        const rootNode = { name: rootName, id: rootId };
        roots.push(rootNode);
        nodeMap.set(rootMapKey, rootNode);
      }

      let parentMapKey = rootMapKey;

      for (let level = 1; level < keyParts.length; level++) {
        const col = resolvedCols[level];
        if (!col || !item[col]) break;

        const childName: string = item[col];
        const childValueCol = resolvedValueCols[level] || col;
        const childRawId = String(item[childValueCol] ?? childName);
        const childMapKey = `${parentMapKey}||${childRawId}`;

        if (!nodeMap.has(childMapKey)) {
          const childNode = { name: childName, id: childRawId };
          nodeMap.set(childMapKey, childNode);
          const parentNode = nodeMap.get(parentMapKey)!;
          (parentNode.children ?? (parentNode.children = [])).push(childNode);
        }

        parentMapKey = childMapKey;
      }
    }
    return roots;
  }

  filterTableData(selectedFilterObject: any, tableData: any[]): any[] {
    const activeKeys = Object.keys(selectedFilterObject ?? {}).filter(
      key => key !== 'attributes' && Array.isArray(selectedFilterObject[key]?.values) && selectedFilterObject[key].values.length > 0
    );

    if (activeKeys.length === 0 || !Array.isArray(tableData) || tableData.length === 0) {
      return tableData;
    }

    const normalizedColMap = new Map<string, string>();
    for (const row of tableData) {
      for (const key of Object.keys(row ?? {})) {
        const nk = this.norm(key);
        if (!normalizedColMap.has(nk)) {
          normalizedColMap.set(nk, key);
        }
      }
    }
    const normalizedColKeys = Array.from(normalizedColMap.keys());

    const resolveColumnNormKey = (filterKey: string): string | undefined => {
      const normalizedFilterKey = this.norm(filterKey);
      if (normalizedColMap.has(normalizedFilterKey)) {
        return normalizedFilterKey;
      }
      return normalizedColKeys.find((nk: string) =>
        nk.includes(normalizedFilterKey) || normalizedFilterKey.includes(nk)
      );
    };

    type MultiCheck = { kind: 'multi'; colNorm?: string; selected: Set<string> };
    type HierCheck = { kind: 'hier'; paths: Array<Array<[string, string]>> };
    type CompiledCheck = MultiCheck | HierCheck;

    const compiledChecks: CompiledCheck[] = [];
    const processedFilterKeys = new Set<string>();

    for (const filterKey of activeKeys) {
      const filterEntry = selectedFilterObject[filterKey];
      const canonicalFilterKey = this.norm(String(filterEntry?.queryKey ?? filterEntry?.displayKey ?? filterKey));
      if (canonicalFilterKey && processedFilterKeys.has(canonicalFilterKey)) {
        continue;
      }
      const values = filterEntry?.values ?? [];

      if (this.isHierarchalType(filterEntry?.type)) {
        const seenPath = new Set<string>();
        const paths: Array<Array<[string, string]>> = values
          .map((pathObj: any) => {
            if (!pathObj || typeof pathObj !== 'object') return [];
            return Object.keys(pathObj)
              .map((pathKey: string) => [this.norm(pathKey), this.norm(String(pathObj[pathKey] ?? ''))] as [string, string])
              .filter((pair: [string, string]) => pair[0] !== '' && pair[1] !== '');
          })
          .filter((pairs: Array<[string, string]>) => {
            if (pairs.length === 0) return false;
            const sk = JSON.stringify(pairs);
            if (seenPath.has(sk)) return false;
            seenPath.add(sk);
            return true;
          });

        if (paths.length > 0) {
          compiledChecks.push({ kind: 'hier', paths });
          if (canonicalFilterKey) processedFilterKeys.add(canonicalFilterKey);
        }
        continue;
      }

      const selectedSet = new Set<string>(
        values
          .map((item: any) => this.norm(String(item?.name ?? item?.value ?? item ?? '')))
          .filter(Boolean)
      );
      if (selectedSet.size === 0) {
        continue;
      }

      let colNorm = resolveColumnNormKey(filterKey);

      // Value-based fallback: infer column once for unmatched keys such as app_filter.
      if (!colNorm) {
        outer: for (const nk of normalizedColKeys) {
          const col = normalizedColMap.get(nk)!;
          for (const row of tableData) {
            const rowVal = this.norm(String(row?.[col] ?? ''));
            if (selectedSet.has(rowVal)) {
              colNorm = nk;
              break outer;
            }
          }
        }
      }

      compiledChecks.push({ kind: 'multi', colNorm, selected: selectedSet });
      if (canonicalFilterKey) processedFilterKeys.add(canonicalFilterKey);
    }

    if (compiledChecks.length === 0) {
      return tableData;
    }

    const filtered: any[] = [];
    for (const row of tableData) {
      const rowNormMap = new Map<string, string>();
      const rowNormValues = new Set<string>();
      for (const key of Object.keys(row ?? {})) {
        const nk = this.norm(key);
        const nv = this.norm(String(row[key] ?? ''));
        if (!rowNormMap.has(nk)) {
          rowNormMap.set(nk, nv);
        }
        if (nv) {
          rowNormValues.add(nv);
        }
      }

      let pass = true;
      for (const check of compiledChecks) {
        if (check.kind === 'hier') {
          let anyPathMatched = false;
          for (const pairs of check.paths) {
            let matched = true;
            for (const [nk, nv] of pairs) {
              if ((rowNormMap.get(nk) ?? '') !== nv) {
                matched = false;
                break;
              }
            }
            if (matched) {
              anyPathMatched = true;
              break;
            }
          }
          if (!anyPathMatched) {
            pass = false;
            break;
          }
          continue;
        }

        if (check.colNorm) {
          const rowVal = rowNormMap.get(check.colNorm) ?? '';
          if (!check.selected.has(rowVal)) {
            pass = false;
            break;
          }
        } else {
          let found = false;
          for (const rowVal of rowNormValues) {
            if (check.selected.has(rowVal)) {
              found = true;
              break;
            }
          }
          if (!found) {
            pass = false;
            break;
          }
        }
      }

      if (pass) {
        filtered.push(row);
      }
    }

    return filtered;
  }

  cacheData(key: string, data: any) {
    this.cachedData.set(key, data);
  }

  getCachedData(key: string): any | undefined {
    return this.cachedData.get(key);
  }

  publishSelectedFilterEvent(event: any): void {
    const report = String(event?.reportNameFromQueryParams ?? '');
    const page = String(event?.currentPage?.key ?? event?.page ?? '');
    const tab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const filterName = String(event?.filterName ?? '');

    const eventKey = `${report}||${page}||${tab}||${filterName}`;
    if (this.selectedFilterEventMap.has(eventKey)) {
      this.selectedFilterEventMap.delete(eventKey);
    }

    this.selectedFilterEventMap.set(eventKey, event);
    this.selectedFilterEventSubject.next(event);
  }

  getSelectedFilterEvents(): Observable<any> {
    return this.selectedFilterEventSubject.asObservable();
  }

  getLatestSelectedFilterEvent(reportNameFromQueryParams: string, pageKey: string, tabKey: string): any | null {
    const report = String(reportNameFromQueryParams ?? '');
    const page = String(pageKey ?? '');
    const tab = String(tabKey ?? '');

    const events = Array.from(this.selectedFilterEventMap.values()).reverse();
    for (const event of events) {
      const eventReport = String(event?.reportNameFromQueryParams ?? '');
      const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
      const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
      if (eventReport === report && eventPage === page && eventTab === tab) {
        return event;
      }
    }

    return null;
  }

  getTablePayload(selectedFilters: any): any {
    const payload:any = {};
    const keys = Object.keys(selectedFilters || {});
    const processedQueryKeys = new Set<string>();
    keys.forEach(key => {
      const entry = selectedFilters[key];
      if(!entry) return;
      const queryKey = String(entry.queryKey || key);
      if(processedQueryKeys.has(queryKey)) return;

      if(entry.type === 'multiselect' && entry.values && entry.values.length > 0){
        //Add check for __selectAll_ value in the selected filters and if it's true, then we need to send all the values for that filter except the ones in __excludeNamest
        if(!entry.values.some((item: any) => item?.__selectAll__ === true )){

          payload[queryKey] = entry.values.map((item: any) => item.value);
          processedQueryKeys.add(queryKey);
        }
          
      }else if(this.isHierarchalType(entry.type) && entry.values){
        const queryMappedEntry = selectedFilters[queryKey];
        const effectiveHierarchyEntry = (this.isHierarchalType(queryMappedEntry?.type) && queryMappedEntry?.isQueryMappedEntry)
          ? queryMappedEntry
          : entry;


        if(!effectiveHierarchyEntry.values.some((item: any) => item?.__selectAll__ === true )){
          const mappedPaths = this.mapHierarchySelectionToQueryKeys(effectiveHierarchyEntry);
          const obj = this.groupHierarchyValuesByKey(mappedPaths);
          if(Object.keys(obj).length > 0){
            for(const [k,v] of Object.entries(obj)){
              payload[k] = v;
              processedQueryKeys.add(k);
            }
          }
          
        }
      }
    });
    return payload;
  }

  clearAllCachedData() {
    this.cachedData.clear();
    this.selectedFilterEventMap.clear();
  }

  private mapHierarchySelectionToQueryKeys(entry: any): any[] {
    const values: any[] = Array.isArray(entry?.values) ? entry.values : [];
    if (values.length === 0) return [];

    const displayKeys = String(entry?.displayPath ?? '').split(':').map((k: string) => k.trim()).filter(Boolean);
    const queryKeys = String(entry?.queryPath ?? '').split(':').map((k: string) => k.trim()).filter(Boolean);

    if (displayKeys.length === 0 || queryKeys.length === 0) {
      return values;
    }

    const keyMap = new Map<string, string>();
    const normalizedKeyMap = new Map<string, string>();
    displayKeys.forEach((displayKey: string, index: number) => {
      const mappedKey = queryKeys[index] || displayKey;
      keyMap.set(displayKey, mappedKey);
      normalizedKeyMap.set(this.norm(displayKey), mappedKey);
    });

    const convertPath = (pathObj: any): any => {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) return pathObj;
      const converted: any = {};
      Object.keys(pathObj).forEach((pathKey: string) => {
        const mappedKey = keyMap.get(pathKey) || normalizedKeyMap.get(this.norm(pathKey)) || pathKey;
        converted[mappedKey] = pathObj[pathKey];
      });
      return converted;
    };

    const allSentinel = values.find((v: any) => v?.__selectAll__ === true);
    if (allSentinel) {
      const excludePaths = Array.isArray(allSentinel.__excludePaths)
        ? allSentinel.__excludePaths.map((pathObj: any) => convertPath(pathObj))
        : undefined;
      return excludePaths && excludePaths.length > 0
        ? [{ __selectAll__: true, __excludePaths: excludePaths }]
        : [{ __selectAll__: true }];
    }

    return values.map((pathObj: any) => convertPath(pathObj));
  }

  private groupHierarchyValuesByKey(paths: any[]): Record<string, any[]> {
    const grouped: Record<string, any[]> = {};
    if (!Array.isArray(paths)) return grouped;

    for (const pathObj of paths) {
      if (!pathObj || typeof pathObj !== 'object' || Array.isArray(pathObj)) continue;
      if (pathObj.__selectAll__ === true) continue;

      Object.keys(pathObj).forEach((key: string) => {
        if (!grouped[key]) {
          grouped[key] = [];
        }

        const value = pathObj[key];
        if (value !== undefined && value !== null && value !== '' && !grouped[key].includes(value)) {
          grouped[key].push(value);
        }
      });
    }

    return grouped;
  }


  

  buildTreeForHierarchyDelimiter(filter:any, data: any[] , splitClause:string){
    const splitPaths = String(splitClause ?? '').split('::');
    const parentPath = splitPaths[0] ? splitPaths[0].trim() : '';
    const childPath = splitPaths[1] ? splitPaths[1].trim() : '';
    const hierarchyDelimiter = " " +childPath + " ";
    const nameMeta = this.resolveObjectMapping(filter?.name);
    const queryMeta = this.resolveObjectMapping(filter?.queryKey);
    const displayFieldCandidates = [nameMeta.displayKey].filter(Boolean);
    // Keep ID candidates strictly to the queryKey — do NOT include the display key here,
    // otherwise the row-key scan may match the name column first when it appears earlier
    // in the object than the ID column.
    const idOnlyCandidates = [queryMeta.queryKey].filter(c => !!c && c !== nameMeta.displayKey);

    const tree: TreeNode[] = [];
    const rootMap = new Map<string, TreeNode>();
    const childMap = new Map<string, TreeNode>();

    let resolvedDisplayKey: string | undefined;
    let resolvedIdKey: string | undefined;

    for (const item of data ?? []) {
      const keys = Object.keys(item ?? {});

      if (!resolvedDisplayKey) {
        resolvedDisplayKey = keys.find((key: string) =>
          displayFieldCandidates.some((candidate: string) => this.norm(key) === this.norm(candidate))
        );
      }

      if (!resolvedIdKey) {
        resolvedIdKey = keys.find((key: string) =>
          idOnlyCandidates.some((candidate: string) => this.norm(key) === this.norm(candidate))
        );
      }

      if (resolvedDisplayKey && resolvedIdKey) {
        break;
      }
    }

    // For hierarchal-delimiter we need both fields:
    // - name from filter.name column
    // - id from filter.queryKey column
    if (!resolvedDisplayKey || !resolvedIdKey) {
      return tree;
    }

    const displayKey = String(resolvedDisplayKey ?? '').trim();
    const idKey = String(resolvedIdKey ?? '').trim();
    if (!displayKey || !idKey) {
      return tree;
    }

    const tokenize = (value: string, delimiter: string): string[] => {
      if (!value) return [];
      if (!delimiter) return [value.trim()].filter(Boolean);
      return value.split(delimiter).map((part: string) => part.trim()).filter(Boolean);
    };

    const upsertRoot = (id: string, name: string): TreeNode => {
      const key = String(id ?? '').trim();
      if (!rootMap.has(key)) {
        const node: TreeNode = { id: key, name: String(name ?? '').trim(), children: [] };
        rootMap.set(key, node);
        tree.push(node);
      }
      return rootMap.get(key)!;
    };

    const upsertChild = (parent: TreeNode, childId: string, childName: string): void => {
      const normalizedChildId = String(childId ?? '').trim();
      const compositeChildId = normalizedChildId
        ? `${parent.id}${hierarchyDelimiter}${normalizedChildId}`
        : String(parent.id ?? '').trim();
      const childKey = compositeChildId;
      if (childMap.has(childKey)) {
        return;
      }

      const childNode: TreeNode = { id: compositeChildId, name: String(childName ?? '').trim(), children: [] };
      childMap.set(childKey, childNode);
      (parent.children ?? (parent.children = [])).push(childNode);
    };

    const addSelfParentChild = (idToken: string, nameToken: string): void => {
      const id = String(idToken ?? '').trim();
      const name = String(nameToken ?? '').trim() || id;
      if (!id && !name) return;

      const root = upsertRoot(id || name, name || id);
      upsertChild(root, id || name, name || id);
    };

    for (const row of data ?? []) {
      const rawId = String(row?.[idKey] ?? '').trim();
      const rawName = String(row?.[displayKey] ?? '').trim();
      if (!rawId && !rawName) {
        continue;
      }

      const idGroups = tokenize(rawId, parentPath);
      const nameGroups = tokenize(rawName, parentPath);

      // Parse each parent-delimited group independently.
      // Example:
      // IDs:   "AH_L0_004 - AH_L1_011,AH_L0_002 - AH_L1_004"
      // Names: "Vendor Hosted,Public Cloud - GCP"
      // => Group-1 parent/child from first pair, Group-2 parent/child from second pair.
      const maxGroups = Math.max(idGroups.length, nameGroups.length);
      for (let groupIndex = 0; groupIndex < maxGroups; groupIndex++) {
        const groupId = idGroups[groupIndex] ?? '';
        const groupName = nameGroups[groupIndex] ?? '';
        const idTokens = tokenize(groupId, childPath);
        const nameTokens = tokenize(groupName, childPath);
        if (idTokens.length === 0 && nameTokens.length === 0) {
          continue;
        }

        const tokenCount = Math.max(idTokens.length, nameTokens.length);

        // Scenario 2 and 3: single token values become self parent-child nodes.
        if (tokenCount <= 1) {
          addSelfParentChild(idTokens[0] ?? '', nameTokens[0] ?? '');
          continue;
        }

        // Multiple tokens in one group: first token is parent, rest are children.
        const parentId = String(idTokens[0] ?? '').trim();
        const parentName = String(nameTokens[0] ?? '').trim() || parentId;
        if (!parentId && !parentName) {
          continue;
        }

        const parentNode = upsertRoot(parentId || parentName, parentName || parentId);

        for (let i = 1; i < tokenCount; i++) {
          const childId = String(idTokens[i] ?? '').trim();
          const childName = String(nameTokens[i] ?? '').trim() || parentName || childId;
          if (!childId && !childName) {
            continue;
          }
          upsertChild(parentNode, childId || childName, childName || childId);
        }
      }
    }

    return tree;
  }

}
